import {
	skipToken,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import {
	ArrangementList,
	ArrangementSurface,
	AuthorView,
	createArrangement,
	getArrangement,
	listArrangements,
	PutArrangement,
	putArrangement,
	removeArrangement,
	reorderArrangements,
} from "../../services/arrangements.service";

// A refusal (403/404) or a rejected save (400) is an answer, not a blip — never retry it.
const retryUnlessRefused = (count: number, error: unknown): boolean => {
	const status = (error as { statusCode?: number })?.statusCode;
	return status !== 400 && status !== 403 && status !== 404 && count < 2;
};

/** SIMS-8562: `id` picks one of the surface's arrangements; none is SIMS-8555's. */
export const arrangementKey = (
	programId: string | undefined,
	surface: ArrangementSurface,
	id?: string | null,
): string[] => ["arrangement", programId ?? "", surface, id ?? ""];

export const arrangementListKey = (
	programId: string | undefined,
	surface: ArrangementSurface,
): string[] => ["arrangement-list", programId ?? "", surface];

/** SIMS-8555 — a surface's arrangement as the arrangement screen shows it. */
export const useArrangement = (
	programId: string | undefined,
	surface: ArrangementSurface,
	id?: string | null,
) =>
	useQuery({
		queryKey: arrangementKey(programId, surface, id),
		queryFn: programId
			? () => getArrangement(programId, surface, id ?? undefined)
			: skipToken,
		retry: retryUnlessRefused,
	});

/** SIMS-8562 — the surface's arrangements, in list order. */
export const useArrangementList = (
	programId: string | undefined,
	surface: ArrangementSurface,
) =>
	useQuery({
		queryKey: arrangementListKey(programId, surface),
		queryFn: programId ? () => listArrangements(programId, surface) : skipToken,
		retry: retryUnlessRefused,
	});

/**
 * After any change to the list: keep its answer, re-read each arrangement (an
 * order or a removal changes which one a "no id" read returns), and drop the
 * dashboard's cached layout and figures so it resolves the viewer afresh.
 */
const afterListChange = (
	client: ReturnType<typeof useQueryClient>,
	programId: string,
	surface: ArrangementSurface,
	list: ArrangementList,
): void => {
	client.setQueryData(arrangementListKey(programId, surface), list);
	void client.invalidateQueries({
		queryKey: ["arrangement", programId, surface],
	});
	void client.invalidateQueries({ queryKey: ["program-insights-surface"] });
	void client.invalidateQueries({ queryKey: ["program-insights-overview"] });
};

/** SIMS-8562 AC1, AC2 — add a named arrangement for some roles. */
export const useCreateArrangement = (
	programId: string,
	surface: ArrangementSurface,
) => {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (body: { name: string; roleIds: string[] }) =>
			createArrangement(programId, surface, body),
		onSuccess: (list) => afterListChange(client, programId, surface, list),
	});
};

/** SIMS-8562 AC6 — save the list in a new order. */
export const useReorderArrangements = (
	programId: string,
	surface: ArrangementSurface,
) => {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (ids: string[]) => reorderArrangements(programId, surface, ids),
		onSuccess: (list) => afterListChange(client, programId, surface, list),
	});
};

/** SIMS-8562 — remove an arrangement; its viewers fall to the next match. */
export const useRemoveArrangement = (
	programId: string,
	surface: ArrangementSurface,
) => {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (id: string) => removeArrangement(programId, surface, id),
		onSuccess: (list) => afterListChange(client, programId, surface, list),
	});
};

/**
 * Saves the arrangement. The answer is the screen's new view; the dashboard's
 * cached layout and figures are dropped so it reads the arrangement afresh.
 */
export const useSaveArrangement = (
	programId: string,
	surface: ArrangementSurface,
) => {
	const client = useQueryClient();
	return useMutation({
		mutationFn: (body: PutArrangement) =>
			putArrangement(programId, surface, body),
		onSuccess: (view: AuthorView, sent: PutArrangement) => {
			client.setQueryData(arrangementKey(programId, surface, sent.id), view);
			// A name or roles changed, or a first save created the arrangement.
			void client.invalidateQueries({
				queryKey: arrangementListKey(programId, surface),
			});
			void client.invalidateQueries({ queryKey: ["program-insights-surface"] });
			void client.invalidateQueries({
				queryKey: ["program-insights-overview"],
			});
		},
	});
};
